import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { formatPauseRemainingPtBr } from '@/utils/formatAutomationPause'

type CountdownVariant = 'countdown' | 'window' | 'blocked' | 'starting'

type FollowUpStateRow = {
  id: string
  status: 'active' | 'completed' | 'paused' | 'blocked' | 'goal_met' | 'stopped'
  next_fire_at: string | null
  next_fire_reason: string | null
  last_error: string | null
}

export interface FollowUpCountdownState {
  visible: boolean
  label: string
  variant: CountdownVariant
}

const HIDDEN_STATE: FollowUpCountdownState = {
  visible: false,
  label: '',
  variant: 'countdown',
}

interface Params {
  contactId?: string | null
  assistantIsActive?: boolean | null
}

export function useContactFollowUpCountdown({ contactId, assistantIsActive }: Params): FollowUpCountdownState {
  const { user } = useAuth()
  const [rows, setRows] = useState<FollowUpStateRow[]>([])
  const [accountTimezone, setAccountTimezone] = useState('America/Sao_Paulo')
  const [nowMs, setNowMs] = useState(Date.now())

  useEffect(() => {
    if (!user?.account_id || !supabase) return
    const client = supabase

    let mounted = true
    const loadTimezone = async () => {
      const { data } = await client
        .from('accounts')
        .select('timezone')
        .eq('id', user.account_id)
        .maybeSingle()

      if (mounted && data?.timezone) {
        setAccountTimezone(data.timezone)
      }
    }

    loadTimezone()
    return () => {
      mounted = false
    }
  }, [user?.account_id])

  useEffect(() => {
    if (!contactId || !assistantIsActive || !supabase) {
      setRows([])
      return
    }
    const client = supabase

    let mounted = true
    const loadStates = async () => {
      const { data, error } = await client
        .from('account_contact_follow_up_states')
        .select('id, status, next_fire_at, next_fire_reason, last_error')
        .eq('account_contact_id', contactId)
        .order('next_fire_at', { ascending: true })
        .limit(10)

      if (error) {
        console.error('Failed to load follow-up countdown states:', error)
        return
      }

      if (mounted) {
        setRows((data || []) as FollowUpStateRow[])
      }
    }

    loadStates()

    const channel = client
      .channel(`followup-countdown-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contact_follow_up_states',
          filter: `account_contact_id=eq.${contactId}`,
        },
        () => loadStates()
      )
      .subscribe()

    return () => {
      mounted = false
      client.removeChannel(channel)
    }
  }, [contactId, assistantIsActive])

  useEffect(() => {
    if (!contactId || !assistantIsActive) return
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [contactId, assistantIsActive])

  return useMemo(() => {
    if (!contactId || !assistantIsActive) {
      return HIDDEN_STATE
    }

    const activeWithFireAt = rows.find(
      (row) => row.status === 'active' && !!row.next_fire_at
    )

    if (activeWithFireAt?.next_fire_at) {
      const fireMs = new Date(activeWithFireAt.next_fire_at).getTime()
      const diffMs = fireMs - nowMs

      if (diffMs <= 0) {
        return {
          visible: true,
          label: 'Follow-up iniciando...',
          variant: 'starting',
        }
      }

      const remaining = formatPauseRemainingPtBr(diffMs)

      if (activeWithFireAt.next_fire_reason === 'outside_window') {
        const localTime = new Intl.DateTimeFormat('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: accountTimezone,
        }).format(new Date(activeWithFireAt.next_fire_at))

        return {
          visible: true,
          label: `Aguardando janela ate ${localTime} (${remaining})`,
          variant: 'window',
        }
      }

      return {
        visible: true,
        label: `Follow-up em ${remaining}`,
        variant: 'countdown',
      }
    }

    const goalMet = rows.find((row) => row.status === 'goal_met')
    if (goalMet) {
      return {
        visible: true,
        label: 'Follow-up: meta atingida ✓',
        variant: 'starting',
      }
    }

    const paused = rows.find((row) => row.status === 'paused')
    if (paused) {
      const pausedLabel =
        paused.next_fire_reason === 'human_active'
          ? 'Follow-up pausado: atendente na conversa'
          : paused.next_fire_reason === 'excluded_tag'
            ? 'Follow-up pausado: tag de exclusão'
            : 'Follow-up pausado'
      return {
        visible: true,
        label: pausedLabel,
        variant: 'window',
      }
    }

    const blocked = rows.find((row) => row.status === 'blocked')
    if (blocked) {
      return {
        visible: true,
        label: blocked.last_error || 'Follow-up aguardando primeira mensagem da IA',
        variant: 'blocked',
      }
    }

    return HIDDEN_STATE
  }, [rows, contactId, assistantIsActive, nowMs, accountTimezone])
}
